/**
 * Makes the Judge.me preview badge (stars + "N reviews") at the top of the
 * product column scroll to the Judge.me review carousel further down the page.
 *
 * Judge.me's own badge handler targets #judgeme_product_reviews (its full review
 * widget), which is not installed on this store — so the badge is inert. This
 * points it at the review snippet carousel in the `apps` section instead.
 *
 * The badge is an app block, so its markup cannot be edited in the theme. This
 * upgrades the rendered element in place, and binds on `document` in the capture
 * phase so the wiring survives Judge.me re-rendering the badge and pre-empts
 * Judge.me's own handler on the stars.
 */
(function () {
  "use strict";

  var BADGE = ".jdgm-widget.jdgm-preview-badge";
  var BADGE_BLOCK = '[id*="judge_me_reviews_preview_badge"]';
  var CAROUSEL =
    '#jdgm-review-snippet-wrapper, .jdgm-review-snippet-widget-v2, .jdgm-widget[data-widget="review-snippet"]';

  var OPEN_LABEL = "View reviews";
  var TRIGGER_CLASS = "starfish-reviews-trigger";
  var FLASH_CLASS = "starfish-reviews-flash";
  var FLASH_MS = 1600;

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  /* Section ids are template-instance-specific, so resolve from Judge.me's own
     stable handles and walk up to the section wrapper. */
  function findTarget() {
    var widget = document.querySelector(CAROUSEL);
    if (!widget) return null;
    return widget.closest(".shopify-section") || widget;
  }

  function findBadge() {
    return (
      document.querySelector(".wt-product__info " + BADGE) ||
      document.querySelector(BADGE_BLOCK + " " + BADGE) ||
      document.querySelector(BADGE_BLOCK)
    );
  }

  function isProductBadge(el) {
    return Boolean(el.closest(".wt-product__info") || el.closest(BADGE_BLOCK));
  }

  /* Judge.me reveals the badge after its script boots, and may re-render it. */
  function whenBadgeReady(callback) {
    var existing = findBadge();
    if (existing) {
      callback(existing);
      return;
    }

    var timer;
    var observer = new MutationObserver(function () {
      var el = findBadge();
      if (!el) return;
      observer.disconnect();
      clearTimeout(timer);
      callback(el);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    timer = setTimeout(function () {
      observer.disconnect();
    }, 15000);
  }

  /* The header is sticky only at some breakpoints — measure, don't assume. */
  function headerOffset() {
    var header = document.querySelector(".page-header");
    if (!header) return 0;
    var position = window.getComputedStyle(header).position;
    if (position !== "sticky" && position !== "fixed") return 0;
    return header.getBoundingClientRect().height;
  }

  function labelFor(el) {
    var stars = el.querySelector(".jdgm-prev-badge__stars");
    var text = el.querySelector(".jdgm-prev-badge__text");
    var parts = [];
    if (stars && stars.getAttribute("aria-label")) {
      parts.push("Rated " + stars.getAttribute("aria-label"));
    }
    if (text) parts.push(text.textContent);
    parts.push(OPEN_LABEL);
    return parts.join(", ").replace(/\s+/g, " ").trim();
  }

  /* Judge.me marks the star span role="button" tabindex="0" for its own
     scroll-to-widget behaviour. Collapse that into one tab stop on the wrapper. */
  function upgradeBadge(el, target) {
    el.classList.add(TRIGGER_CLASS);
    el.setAttribute("role", "button");
    el.setAttribute("tabindex", "0");
    el.setAttribute("aria-label", labelFor(el));
    if (target.id) el.setAttribute("aria-controls", target.id);

    var stars = el.querySelector(".jdgm-prev-badge__stars");
    if (stars) {
      stars.removeAttribute("role");
      stars.setAttribute("tabindex", "-1");
      stars.setAttribute("aria-hidden", "true");
    }
  }

  function flash(el) {
    if (prefersReducedMotion()) return;
    el.classList.remove(FLASH_CLASS);
    void el.offsetWidth;
    el.classList.add(FLASH_CLASS);
    setTimeout(function () {
      el.classList.remove(FLASH_CLASS);
    }, FLASH_MS);
  }

  function scrollToReviews() {
    /* Re-resolve: Judge.me can populate the carousel after we wire the badge. */
    var target = findTarget();
    if (!target) return;

    var top =
      target.getBoundingClientRect().top +
      window.pageYOffset -
      headerOffset() -
      16;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });

    /* Move keyboard focus with the scroll so tabbing continues from here. */
    target.classList.add("starfish-reviews-target");
    target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });
    flash(target);
  }

  /* Capture phase: Judge.me binds its own click on the stars. */
  function bindTriggers() {
    function handle(event) {
      var trigger = event.target.closest(BADGE);
      if (!trigger || !isProductBadge(trigger)) return;
      if (!trigger.classList.contains(TRIGGER_CLASS)) return;
      event.preventDefault();
      event.stopPropagation();
      scrollToReviews();
    }

    document.addEventListener("click", handle, true);

    document.addEventListener(
      "keydown",
      function (event) {
        if (
          event.key !== "Enter" &&
          event.key !== " " &&
          event.key !== "Spacebar"
        ) {
          return;
        }
        handle(event);
      },
      true,
    );
  }

  ready(function () {
    var target = findTarget();
    /* No reviews carousel on this page — leave the badge exactly as it is. */
    if (!target) return;

    bindTriggers();

    whenBadgeReady(function (badge) {
      upgradeBadge(badge, target);

      /* Judge.me can repaint the badge and undo the attributes above. */
      new MutationObserver(function () {
        if (badge.isConnected) upgradeBadge(badge, target);
      }).observe(badge, { childList: true, subtree: true });
    });
  });
})();
